// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Button, CircularProgress } from '@mui/material';
import { PageContainer } from '@shared/ui-kit/components/page-container';
import { RebalanceEditor } from '@advisor-app/features/rebalance/components';
import { useAppNavigate } from '@advisor-app/hooks';
import { useAppSelector } from '@advisor-app/store/store';
import { useHandlerRebalance } from '@advisor-app/features/rebalance/hooks/use-handler-rebalance';
import { BackButton } from '@advisor-app/components';
import {
  selectCurrentPortfolio,
  selectTargetPortfolio,
} from '@shared/data-access/slices/portfolio-positions';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigation } from 'react-router';

export const Rebalance = () => {
  const { t } = useTranslation();
  const investmentPortfolio = useAppSelector(selectCurrentPortfolio);

  const weights = useAppSelector(selectTargetPortfolio);

  const navigate = useAppNavigate();
  const navigation = useNavigation();

  const rebalanceProps = useHandlerRebalance();

  const totalWeight = useMemo(
    () =>
      +weights
        .map(asset => asset.weight)
        .reduce((a, b) => a + b, 0)
        .toFixed(2),
    [weights],
  );

  const canRunRebalance = totalWeight === 100;
  const isNavigating = navigation.state === 'loading';

  if (investmentPortfolio.length === 0) return null;

  return (
    <PageContainer
      titlePage={t('common.rebalance', { ns: 'common' })}
      showBottomButtons
      actionBtn={
        <div className="flex space-x-2">
          <BackButton data-testid="btn-back" to="/rebalance" />
          <Button
            color="secondary"
            onClick={() => navigate('/rebalance/summary')}
            disabled={!canRunRebalance || isNavigating}
            data-testid="btn-next"
            endIcon={
              isNavigating ? (
                <CircularProgress size={16} color="secondary" />
              ) : undefined
            }
          >
            {t('common.next', { ns: 'common' })}
          </Button>
        </div>
      }
    >
      <RebalanceEditor {...rebalanceProps} />
    </PageContainer>
  );
};

export default Rebalance;
